import { JSX } from "react";
import { Modal, Table, Button, Tooltip } from "antd";
import {
  StatusSignedIcon,
  StatusDeclinedIcon,
  StatusNewIcon,
  StatusPendingIcon,
  StatusPendingGsnIcon,
  CommentIcon,
} from "@/shared/assets/svg/icons";
import { formatDate, formatMoscowDatetime } from "@/shared/utils";
import { UserBubble } from "@shared/ui";
import { useGetItemsVersion } from "../../../model";

type Item = any;

type Props = {
  open: boolean;
  onCancel: () => void;
  sectionFourListId?: string;
  itemNoVersionId?: string;
};

export const JournalVersionModal = ({
  open,
  onCancel,
  sectionFourListId,
  itemNoVersionId,
}: Props) => {
  const { data, isLoading } = useGetItemsVersion(
    sectionFourListId,
    itemNoVersionId,
  );

  const versions =
    data?.section4List?.map((entry) => ({
      ...entry.versionInfo,
      itemPartOne: entry.content?.itemPartOne,
      itemPartTwo: entry.content?.itemPartTwo,
      signer:
        entry.content?.itemPartTwo?.defectFixingInspectionEmployee ||
        entry.content?.itemPartOne?.inspectionEmployee,
      actionDetails: entry.versionInfo?.actionDetails,
    })) ?? [];

  const statusIcons: Record<string, { icon: JSX.Element; tooltip: string }> = {
    NEW: { icon: <StatusNewIcon />, tooltip: "Новый" },
    AWAITING_SIGNATURE: {
      icon: <StatusPendingIcon />,
      tooltip: "Ожидает подписания",
    },
    REJECTED: { icon: <StatusDeclinedIcon />, tooltip: "Отклонено" },
    SIGNED: { icon: <StatusSignedIcon />, tooltip: "Подписано" },
    SENT_TO_GSN: {
      icon: <StatusPendingGsnIcon />,
      tooltip: "Отправлено в ГСН",
    },
  };

  const columns = [
    {
      title: "Сведения о проведении строительного контроля",
      dataIndex: "itemPartOne",
      render: (_: any, row: Item) => {
        const user = row.itemPartOne?.inspectionEmployee;
        const date = row.updateDate;
        const userId = row?.updateUserId;
        return (
          <div>
            {user && (
              <div className="journal-version-modal-user-block">
                <UserBubble userId={userId || ""}>
                  <span className="journal-version-modal-user-name">
                    {user.surname} {user.firstName}
                  </span>
                </UserBubble>
                <span className="journal-version-modal-datetime">
                  {formatMoscowDatetime(date)}
                </span>
              </div>
            )}
            <div className="journal-version-modal-pre-wrap">
              {row.itemPartOne?.constructionControlInfo || "-"}
            </div>
          </div>
        );
      },
    },

    {
      title: "Выявленные недостатки",
      dataIndex: "itemPartOne",
      render: (_: any, row: Item) =>
        row.itemPartOne?.defectDescriptionText || "-",
    },
    {
      title: "Плановая дата устранения",
      dataIndex: "itemPartOne",
      render: (_: any, row: Item) =>
        formatDate(row.itemPartOne?.defectFixingPlanDate) || "-",
    },
    {
      title: "Уполномоченный представитель по вопросам строительного контроля",
      dataIndex: "itemPartOne",
      render: (_: any, row: Item) => {
        const rep = row.itemPartOne?.inspectionEmployee;
        if (!rep) return "-";
        const fio = `${rep.surname} ${rep.firstName?.[0] ?? ""}.${rep.middleName?.[0] ?? ""}.`;
        const userId =
          row.itemPartOne?.inspectionEmployee?.inspectionEmployeeId;
        return (
          <div>
            <div className="journal-version-modal-rep-row">
              <UserBubble userId={userId}>
                <Button type="link" style={{ padding: 0 }}>
                  {fio}
                </Button>
              </UserBubble>
              {row.actionDetails && (
                <Tooltip title={row.actionDetails}>
                  <CommentIcon />
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Фактическая дата устранения",
      dataIndex: "itemPartTwo",
      render: (_: any, row: Item) =>
        formatDate(row.itemPartTwo?.defectFixingFactDate) || "-",
    },
    {
      title: "Уполномоченный представитель по вопросам строительного контроля",
      dataIndex: "itemPartTwo",
      render: (_: any, row: Item) => {
        const rep = row.itemPartTwo?.defectFixingInspectionEmployee;

        const fio = rep
          ? `${rep.surname} ${rep.firstName?.[0] ?? ""}.${rep.middleName?.[0] ?? ""}.`
          : null;

        return (
          <div className="journal-version-modal-signer-cell">
            <div className="journal-version-modal-version-badge">
              <div className="journal-version-modal-version-stack">
                <div className="journal-version-modal-version-number">
                  Версия {row.versionNumber}
                </div>
                {statusIcons[row.status] && (
                  <Tooltip
                    title={`${statusIcons[row.status].tooltip} ${
                      row.updateDate ? formatMoscowDatetime(row.updateDate) : ""
                    }`}
                  >
                    {statusIcons[row.status].icon}
                  </Tooltip>
                )}
              </div>
            </div>

            {rep ? (
              <>
                <div className="journal-version-modal-rep-row">
                  <UserBubble
                    userId={
                      row?.itemPartTwo?.defectFixingInspectionEmployee
                        ?.defectFixingInspectionEmployeeId || ""
                    }
                  >
                    <Button type="link" style={{ padding: 0 }}>
                      {fio}
                    </Button>
                  </UserBubble>

                  {row.actionDetails && (
                    <Tooltip title={row.actionDetails}>
                      <CommentIcon />
                    </Tooltip>
                  )}
                </div>
              </>
            ) : (
              <div>-</div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={
        <div className="journal-version-modal-footer">
          <Button type="primary" onClick={onCancel}>
            Закрыть
          </Button>
        </div>
      }
      width={1200}
      title={<div className="journal-version-modal-title">Версии записи</div>}
    >
      <Table
        columns={columns}
        dataSource={versions}
        rowKey="itemNoVersionId"
        pagination={false}
        loading={isLoading}
      />
    </Modal>
  );
};
